import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto p-4 pb-24">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-900 mb-6 hover:text-gray-700"
        >
          <ChevronLeft size={20} />
          <span className="ml-1">Back</span>
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-sm text-gray-500 mb-6">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <div className="space-y-6 text-sm text-gray-700">
          {/* Introduction */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">1. Introduction</h2>
            <p className="leading-relaxed">
              Welcome to Hey Sri Lanka ("we," "our," or "us"). By accessing or using our mobile application 
              and services, you agree to be bound by these Terms of Service. If you do not agree to these 
              terms, please do not use our services.
            </p>
          </section>

          {/* Services */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">2. Our Services</h2>
            <p className="leading-relaxed mb-2">
              Hey Sri Lanka provides AI-powered travel assistance for visitors to Sri Lanka, including:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>AI chat for travel recommendations and information</li>
              <li>Trip planning and itinerary creation tools</li>
              <li>Fare estimation for local transportation</li>
              <li>Place discovery and saving features</li>
              <li>Quote requests for travel services</li>
            </ul>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">3. User Accounts</h2>
            <p className="leading-relaxed">
              You may use certain features without an account. To access full features, you must create 
              an account using Google Sign-In or email registration. You are responsible for maintaining 
              the confidentiality of your account and for all activities under your account.
            </p>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">4. Acceptable Use</h2>
            <p className="leading-relaxed mb-2">You agree not to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Use the service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the service</li>
              <li>Submit false or misleading information</li>
              <li>Use automated systems to access the service without permission</li>
            </ul>
          </section>

          {/* AI Disclaimer */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">5. AI-Generated Content</h2>
            <p className="leading-relaxed">
              Our AI provides recommendations and information based on available data. While we strive 
              for accuracy, AI-generated content may contain errors or outdated information. Always 
              verify important details independently. We are not liable for decisions made based on 
              AI recommendations.
            </p>
          </section>

          {/* Fare Estimates */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">6. Fare Estimates</h2>
            <p className="leading-relaxed">
              Fare estimates are provided as guidance only. Actual fares may vary based on traffic, 
              weather, driver discretion, and other factors. We do not guarantee that drivers will 
              honor estimated fares. Use estimates as a reference for fair pricing.
            </p>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">7. Third-Party Services</h2>
            <p className="leading-relaxed">
              When you request quotes or book services, you may interact with third-party providers. 
              We facilitate connections but are not responsible for the quality, safety, or delivery 
              of third-party services. Your agreements with service providers are separate from these terms.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">8. Intellectual Property</h2>
            <p className="leading-relaxed">
              All content, features, and functionality of Hey Sri Lanka are owned by us and protected 
              by intellectual property laws. You may not copy, modify, or distribute our content 
              without permission.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">9. Limitation of Liability</h2>
            <p className="leading-relaxed">
              Hey Sri Lanka is provided "as is" without warranties of any kind. We are not liable for 
              any indirect, incidental, or consequential damages arising from your use of the service. 
              Our total liability shall not exceed the amount you paid us in the past 12 months.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">10. Changes to Terms</h2>
            <p className="leading-relaxed">
              We may update these terms from time to time. Continued use of the service after changes 
              constitutes acceptance of the new terms. We will notify users of significant changes.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">11. Contact Us</h2>
            <p className="leading-relaxed">
              If you have questions about these Terms of Service, please contact us at{' '}
              <a href="mailto:malintha.levine@gmail.com" className="text-orange-600 hover:underline">
                malintha.levine@gmail.com
              </a>
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} Hey Sri Lanka. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

